import { getDbSafe } from './firebaseConfig';
import {
  doc, collection, addDoc, updateDoc, getDoc, getDocs,
  serverTimestamp, query, orderBy, limit, where, Timestamp
} from 'firebase/firestore';
import { NotificationService } from './api';

// Collections where yaps can live
const YAP_COLLECTIONS = ['yaps', 'forum_posts', 'tribe_posts'];

// ─── Types ────────────────────────────────────────────────────────────────────

export type WatcherActionType =
  | 'mute'
  | 'unmute'
  | 'soft_delete_yap'
  | 'legion_delete'
  | 'ban'
  | 'warn_user'
  | 'hide_content'
  | 'hide_account'
  | 'unhide_account'
  | 'resolve_report'
  | 'dismiss_report'
  | 'override'
  | (string & {});

export type MuteDuration = '1h' | '24h' | '7d' | '30d' | 'permanent';

export interface AuditEntry {
  id?: string;
  watcher_id: string;
  watcher_username: string;
  target_user_id: string;
  action_type: WatcherActionType;
  reason: string;
  timestamp: any; // Firestore Timestamp
  metadata?: Record<string, any>;
}

// ─── Mute / Unmute ────────────────────────────────────────────────────────────

const MUTE_DURATION_MS: Record<MuteDuration, number | null> = {
  '1h':        1 * 60 * 60 * 1000,
  '24h':       24 * 60 * 60 * 1000,
  '7d':        7  * 24 * 60 * 60 * 1000,
  '30d':       30 * 24 * 60 * 60 * 1000,
  'permanent': null,
};

export async function muteUser(
  watcherId: string,
  watcherUsername: string,
  targetUserId: string,
  duration: MuteDuration,
  reason: string,
): Promise<void> {
  const db = await getDbSafe();

  const muteExpiresAt = MUTE_DURATION_MS[duration] !== null
    ? new Date(Date.now() + MUTE_DURATION_MS[duration]!)
    : null;

  // Write mute to the target user's doc
  await updateDoc(doc(db, 'users', targetUserId), {
    isMuted: true,
    muteReason: reason,
    mutedById: watcherId,
    muteExpiresAt: muteExpiresAt ? Timestamp.fromDate(muteExpiresAt) : null,
  });

  // Target notification
  await NotificationService.sendNotification(
    targetUserId,
    'WATCHER_MOD',
    'Account Muted',
    `You have been muted by @${(watcherUsername || 'a moderator').replace(/^@/, '')} for ${duration}. Reason: ${reason}`,
    { watcherId, watcherUsername, duration }
  );

  // Audit log (non-critical)
  try {
    await logAuditAction({
      watcher_id: watcherId,
      watcher_username: watcherUsername,
      target_user_id: targetUserId,
      action_type: 'mute',
      reason,
      timestamp: serverTimestamp(),
      metadata: { duration, muteExpiresAt: muteExpiresAt?.toISOString() ?? 'permanent' },
    });
  } catch (auditErr) {
    console.warn('[WatcherService] Audit log write failed (non-critical):', auditErr);
  }
}

export async function unmuteUser(
  watcherId: string,
  watcherUsername: string,
  targetUserId: string,
  reason: string,
): Promise<void> {
  const db = await getDbSafe();

  await updateDoc(doc(db, 'users', targetUserId), {
    isMuted: false,
    muteReason: null,
    mutedById: null,
    muteExpiresAt: null,
  });

  // Audit log (non-critical)
  try {
    await logAuditAction({
      watcher_id: watcherId,
      watcher_username: watcherUsername,
      target_user_id: targetUserId,
      action_type: 'unmute',
      reason,
      timestamp: serverTimestamp(),
    });
  } catch (auditErr) {
    console.warn('[WatcherService] Audit log write failed (non-critical):', auditErr);
  }
}

// ─── Soft-Delete Yap ─────────────────────────────────────────────────────────

export async function softDeleteYap(
  watcherId: string,
  watcherUsername: string,
  yapId: string,
  authorId: string,
  yapContent: string,
  reason: string,
): Promise<void> {
  const db = await getDbSafe();

  // ── 1. Find the yap across all possible collections ──────────────────────
  let foundRef: ReturnType<typeof doc> | null = null;
  for (const col of YAP_COLLECTIONS) {
    const ref = doc(db, col, yapId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
      foundRef = ref;
      break;
    }
  }

  if (!foundRef) {
    throw new Error(`Yap ${yapId} not found in any collection`);
  }

  // ── 2. Soft-delete — set BOTH flags so every feed filter picks it up ──────
  await updateDoc(foundRef, {
    is_hidden: true,
    deleted: true,
    status: 'archived',
    deletedAt: serverTimestamp(),
    deletedBy: watcherId,
    deletedReason: reason,
  });

  // ── 2b. Immediately evict from any mounted feed (client-side) ─────────────
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('yapDeleted', { detail: { yapId } }));
  }

  // ── 3. Deduplicate: skip notification if one already exists for this yap ──
  const existingQuery = query(
    collection(db, 'notifications'),
    where('metadata.yapId', '==', yapId),
    where('type', '==', 'watcher_deletion'),
    limit(1),
  );
  const existingSnap = await getDocs(existingQuery);

  if (existingSnap.empty && authorId) {
    await NotificationService.sendNotification(
      authorId,
      'WATCHER_MOD',
      'Post Removed',
      `Your post: "${yapContent.slice(0, 80)}${yapContent.length > 80 ? '…' : ''}" was removed by @${(watcherUsername || 'a moderator').replace(/^@/, '')}. Reason: ${reason}`,
      { yapId, reason, watcherId, watcherUsername }
    );
  }

  // ── 4. Audit log (non-critical — log error but don't rethrow) ────────────
  try {
    await logAuditAction({
      watcher_id: watcherId,
      watcher_username: watcherUsername,
      target_user_id: authorId,
      action_type: 'soft_delete_yap',
      reason,
      timestamp: serverTimestamp(),
      metadata: { yapId, content: yapContent.slice(0, 200) },
    });
  } catch (auditErr) {
    console.warn('[WatcherService] Audit log write failed (non-critical):', auditErr);
  }
}

// ─── Audit Log ───────────────────────────────────────────────────────────────

export async function logAuditAction(entry: Omit<AuditEntry, 'id'>): Promise<void> {
  const db = await getDbSafe();
  // Standardize mapping to moderation_logs
  await addDoc(collection(db, 'moderation_logs'), {
    actorId: entry.watcher_id,
    actorUsername: entry.watcher_username,
    targetId: entry.target_user_id,
    actionType: entry.action_type,
    reason: entry.reason,
    timestamp: entry.timestamp || serverTimestamp(),
    metadata: entry.metadata || {}
  });
}

/**
 * Normalize a raw `moderation_logs` document into an AuditEntry. Several writers
 * use slightly different field names — logAuditAction (actorId/actorUsername/
 * targetId/actionType), the hide-account CF (type/targetUserId/modUserId), and
 * LegionService — so we coalesce them all into one shape for the audit feed.
 */
export function normalizeAuditDoc(id: string, d: any): AuditEntry {
  return {
    id,
    watcher_id: d.actorId || d.modUserId || d.watcher_id || d.actorUid || '',
    watcher_username: d.actorUsername || d.watcher_username || d.actorName || '',
    target_user_id: d.targetId || d.targetUserId || d.target_user_id || '',
    action_type: (d.actionType || d.type || d.action || 'action') as any,
    reason: d.reason || '',
    timestamp: d.timestamp,
    metadata: d.metadata || d,
  };
}

export async function fetchAuditLog(limitCount = 50): Promise<AuditEntry[]> {
  const db = await getDbSafe();
  // Read from moderation_logs — the collection every mod action actually writes
  // to (the old `watcher_audit_log` was never populated).
  const q = query(
    collection(db, 'moderation_logs'),
    orderBy('timestamp', 'desc'),
    limit(limitCount),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => normalizeAuditDoc(d.id, d.data()));
}

// ─── User Mute Status Check ───────────────────────────────────────────────────

export async function checkAndLiftExpiredMute(userId: string): Promise<boolean> {
  const db = await getDbSafe();
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) return false;

  const data = userSnap.data();
  if (!data.isMuted) return false;

  // Permanent mute — never lift
  if (!data.muteExpiresAt) return true;

  const expiresAt: Timestamp = data.muteExpiresAt;
  if (expiresAt.toDate() <= new Date()) {
    // Mute has expired — lift automatically
    await updateDoc(doc(db, 'users', userId), {
      isMuted: false,
      muteReason: null,
      mutedById: null,
      muteExpiresAt: null,
    });
    return false;
  }

  return true;
}

// ─── Report to Watchers ─────────────────────────────────────────────────────

export async function reportToWatchers(
  reporterId: string,
  targetId: string,
  targetType: 'user' | 'yap' | 'reply' | 'shout' | 'event' | 'event_reply' | 'event_comment' | 'dm' | 'forum_post' | 'tribe_post' | 'comment' | 'room' | 'room_message',
  reason: string,
  metadata: any = {}
): Promise<void> {
  const db = await getDbSafe();

  // Resolve the owner of the reported content. Each caller passes the owner's
  // id under a type-specific metadata key; for a reported user the target IS
  // the owner. This powers the normalized `targetUserId` field below.
  const targetUserId =
    metadata?.targetUserId ||
    metadata?.authorId ||
    metadata?.shouterId ||
    metadata?.ownerId ||
    (targetType === 'user' ? targetId : '');

  // 1. Create the normalized report document.
  // Canonical schema (spec): type / targetId / targetUserId / reportedByUserId
  // / reason / status / timestamp. Legacy aliases (reporterId / reportedId /
  // targetType / category) are kept so the existing mod queue keeps reading.
  const reportData = {
    // canonical
    type: targetType,
    targetId,
    targetUserId,
    reportedByUserId: reporterId,
    reason,
    status: 'pending',
    timestamp: serverTimestamp(),
    // legacy aliases (do not remove — read by WatcherActionPanel/dashboards)
    reporterId,
    reportedId: targetId,
    targetType,
    category: metadata?.category || 'other',
    metadata: metadata || {}
  };

  await addDoc(collection(db, 'reports'), reportData);

  // 2. Alert all watchers (non-blocking)
  try {
    const watchersQuery = query(
      collection(db, 'users'),
      where('rank', '==', 'watcher')
    );
    const watchersSnap = await getDocs(watchersQuery);
    
    const notificationPromises = watchersSnap.docs.map(watcherDoc => {
      return NotificationService.sendNotification(
        watcherDoc.id,
        'WATCHER_MOD',
        'New Content Report',
        `A "${targetType}" has been reported for: ${reason}`,
        { ...metadata, reportId: targetId, targetType }
      );
    });

    await Promise.all(notificationPromises);
  } catch (err) {
    console.warn('[WatcherService] Failed to notify watchers:', err);
  }
}

// ─── Account hiding (portfolio violations) ──────────────────────────────────
// Thin client wrappers around the server-enforced Cloud Functions. The server
// re-verifies Legion/Watcher rank — these helpers never decide authorization.

export async function hideAccountForPortfolioViolation(targetUserId: string): Promise<void> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const fn = httpsCallable(getFunctions(), 'hideAccountForPortfolioViolation');
  await fn({ targetUserId });
}

export async function unhideAccount(targetUserId: string): Promise<void> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const fn = httpsCallable(getFunctions(), 'unhideAccount');
  await fn({ targetUserId });
}
